// BotMarketplacePage — Browse community bot strategies
// Layout: featured, categories, bot cards with backtest stats, subscribe/clone

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';
import { TfCardList, TfBadge } from '../../components/TfPrimitives';

interface BotListing {
  id: string;
  name: string;
  author: string;
  authorAvatar: string;
  description: string;
  category: 'Breakout' | 'Mean Reversion' | 'Trend' | 'Sniper' | 'Scalper' | 'Grid';
  tier: 'Beginner' | 'Mid' | 'Professional';
  winRate: number;
  trades: number;
  pnlPct: number;
  sharpe: number;
  maxDD: number;
  subs: number;
  price: number; // monthly subscription
  isNew?: boolean;
  isHot?: boolean;
  verified?: boolean;
  emoji: string;
  color: string;
  pairs: string[];
}

const BOTS: BotListing[] = [
  { id: 'b1', name: 'Gold Digger',     author: 'Marcus Chen',  authorAvatar: 'user-1', description: 'High-probability breakouts on GC and SI futures',           category: 'Breakout',       tier: 'Beginner',     winRate: 0.71, trades: 412, pnlPct: 184.2, sharpe: 2.41, maxDD: -6.8,  subs: 8420, price: 0,    isHot: true,  verified: true,  emoji: '🥇', color: '#FF9500', pairs: ['GC', 'SI'] },
  { id: 'b2', name: 'NQ Sniper',       author: 'Aiden Wright', authorAvatar: 'user-3', description: 'Momentum scalps on NQ during US session',                   category: 'Sniper',         tier: 'Professional', winRate: 0.68, trades: 628, pnlPct: 142.0, sharpe: 2.18, maxDD: -8.4,  subs: 5340, price: 29,   verified: true,  emoji: '🎯', color: '#FF3B30', pairs: ['NQ', 'ES'] },
  { id: 'b3', name: 'ES Mean Reverter', author: 'Sofia Petrov', authorAvatar: 'user-2', description: 'Statistical mean reversion on E-mini S&P',                  category: 'Mean Reversion', tier: 'Mid',          winRate: 0.74, trades: 287, pnlPct: 98.4,  sharpe: 1.92, maxDD: -5.2,  subs: 3210, price: 0,    isNew: true,  verified: true,  emoji: '🔁', color: '#007AFF', pairs: ['ES'] },
  { id: 'b4', name: 'Oil Slick',       author: 'Diego Rivera', authorAvatar: 'user-3', description: 'Trend follower for crude oil futures',                       category: 'Trend',          tier: 'Professional', winRate: 0.62, trades: 198, pnlPct: 67.8,  sharpe: 1.65, maxDD: -11.2, subs: 1820, price: 49,                verified: true,  emoji: '🛢️', color: '#1C1C1E', pairs: ['CL'] },
  { id: 'b5', name: 'BTC Grid',        author: 'Lena Voss',    authorAvatar: 'user-4', description: 'Grid trading bot for Bitcoin ranging markets',               category: 'Grid',           tier: 'Mid',          winRate: 0.81, trades: 1024, pnlPct: 45.6, sharpe: 1.45, maxDD: -7.8,  subs: 6450, price: 19,   isHot: true,                   emoji: '🔲', color: '#FF9500', pairs: ['BTC'] },
  { id: 'b6', name: 'Scalper Supreme',  author: 'Yuki Tanaka',  authorAvatar: 'user-4', description: 'Quick scalps on CL and GC during high-volume sessions',      category: 'Scalper',        tier: 'Beginner',     winRate: 0.66, trades: 842, pnlPct: 38.2,  sharpe: 1.32, maxDD: -9.1,  subs: 9120, price: 0,    isNew: true,                    emoji: '⚡', color: '#34C759', pairs: ['CL', 'GC'] },
  { id: 'b7', name: 'Range Rider',     author: 'Aisha Khan',   authorAvatar: 'user-2', description: 'Detects and rides range breakouts on currency futures',     category: 'Breakout',       tier: 'Beginner',     winRate: 0.69, trades: 354, pnlPct: 56.4,  sharpe: 1.78, maxDD: -6.2,  subs: 2870, price: 9,                  verified: true,  emoji: '🏇', color: '#AF52DE', pairs: ['6E', '6B', '6J'] },
  { id: 'b8', name: 'Power Hour',      author: 'Ravi Patel',   authorAvatar: 'user-1', description: 'Volatility breakout during last hour of US session',         category: 'Breakout',       tier: 'Mid',          winRate: 0.65, trades: 287, pnlPct: 72.1,  sharpe: 1.85, maxDD: -8.9,  subs: 1240, price: 15,                                emoji: '💥', color: '#5856D6', pairs: ['ES', 'NQ', 'YM'] },
];

const CATEGORIES = ['All', 'Breakout', 'Mean Reversion', 'Trend', 'Sniper', 'Scalper', 'Grid'] as const;
type Category = typeof CATEGORIES[number];

const SORTS = ['Top rated', 'Most popular', 'Newest', 'Price'] as const;

export function BotMarketplacePage() {
  const nav = useNavigate();
  const [category, setCategory] = useState<Category>('All');
  const [sort, setSort] = useState<typeof SORTS[number]>('Top rated');
  const [subscribed, setSubscribed] = useState<Set<string>>(new Set());

  const filtered = BOTS
    .filter((b) => category === 'All' || b.category === category)
    .sort((a, b) => {
      if (sort === 'Top rated')     return b.sharpe - a.sharpe;
      if (sort === 'Most popular')  return b.subs - a.subs;
      if (sort === 'Newest')        return Number(b.isNew) - Number(a.isNew);
      if (sort === 'Price')         return a.price - b.price;
      return 0;
    });

  function toggleSub(id: string) {
    setSubscribed((curr) => {
      const n = new Set(curr);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Bot marketplace</div>
        <button onClick={() => alert('Search bots')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero — featured + stats */}
      <div className="mx-4 mt-3">
        <div className="rounded-[20px] text-center p-5 text-white" style={{ background: 'linear-gradient(135deg, #5856D6 0%, #007AFF 100%)' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">Bot marketplace</div>
          <div className="mt-1 text-[28px] font-bold tracking-tight">{BOTS.length} strategies</div>
          <div className="mt-1 text-[13px] text-white/85">From top traders · 4 free, 4 premium</div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <span className="rounded-full bg-white/20 backdrop-blur-md px-2.5 py-1 text-[10px] font-semibold">🔥 Hot: Gold Digger</span>
            <span className="rounded-full bg-white/20 backdrop-blur-md px-2.5 py-1 text-[10px] font-semibold">✨ New: ES Mean Reverter</span>
          </div>
        </div>
      </div>

      {/* Category pills */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`botcat-${c}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition',
              category === c ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Sort */}
      <div className="mx-4 mt-3 flex items-center justify-between">
        <span className="text-[11px] text-tf-text-3">{filtered.length} bots</span>
        <button onClick={() => alert('Sort menu')} className="flex items-center gap-1 text-[12px] font-semibold text-tf-blue" data-testid="bot-sort">
          Sort: {sort}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Bot cards */}
      <div className="mx-4 mt-3" data-testid="bot-list">
        <TfCardList>
          {filtered.map((b, i) => {
            const isSub = subscribed.has(b.id);
            return (
              <div
                key={b.id}
                data-testid={`bot-${b.id}`}
                className={['px-4 py-3', i < filtered.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] text-[28px]"
                    style={{ backgroundColor: b.color + '20' }}
                  >
                    {b.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="text-[15px] font-bold text-tf-text-1">{b.name}</h3>
                      {b.verified && <span className="text-tf-blue text-[12px]">✓</span>}
                      {b.isHot && <TfBadge variant="danger">Hot</TfBadge>}
                      {b.isNew && <TfBadge variant="success">New</TfBadge>}
                    </div>
                    <p className="text-[12px] text-tf-text-3 line-clamp-1">{b.description}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <UserAvatar size={16} name={b.author} />
                      <span className="text-[11px] text-tf-text-3">by {b.author}</span>
                      <TierPill tier={b.tier} />
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {b.pairs.map((p) => (
                        <span key={p} className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-bold text-tf-text-3">{p}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Stats strip */}
                <div className="mt-3 grid grid-cols-4 rounded-[10px] border border-tf-border bg-tf-surface-1">
                  <Stat label="Win" value={`${(b.winRate * 100).toFixed(0)}%`} />
                  <Stat label="PnL" value={`+${b.pnlPct.toFixed(0)}%`} color="text-tf-profit" />
                  <Stat label="Sharpe" value={b.sharpe.toFixed(1)} />
                  <Stat label="Max DD" value={`${b.maxDD.toFixed(1)}%`} color="text-tf-loss" />
                </div>
                {/* Actions */}
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] text-tf-text-3">{b.subs.toLocaleString()} subscribers</div>
                    <div className="text-[13px] font-bold text-tf-text-1">{b.price === 0 ? 'Free' : `$${b.price}/mo`}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => nav('/portal/strategy/' + b.id)}
                      data-testid={`bot-view-${b.id}`}
                      className="h-9 rounded-full bg-tf-surface-1 px-3 text-[12px] font-semibold text-tf-text-1 active:opacity-40"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => toggleSub(b.id)}
                      data-testid={`bot-sub-${b.id}`}
                      className={['h-9 rounded-full px-4 text-[12px] font-semibold text-white transition active:opacity-40',
                        isSub ? 'bg-tf-profit' : 'bg-tf-text-1'
                      ].join(' ')}
                    >
                      {isSub ? '✓ Subscribed' : 'Subscribe'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </TfCardList>
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Past performance is not indicative of future results. Subscriptions are non-refundable.</p>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="px-2 py-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-tf-text-3">{label}</div>
      <div className={['text-[12px] font-bold tabular-nums', color || 'text-tf-text-1'].join(' ')}>{value}</div>
    </div>
  );
}
